/**
 * Customizable Countdown Timer
 * Supports timezones, custom styling, and responsive design
 */

class CountdownTimer {
  constructor(elementId, options = {}) {
    this.element = document.getElementById(elementId);
    if (!this.element) {
      console.error(`Element with id "${elementId}" not found`);
      return;
    }

    this.options = {
      endDate: options.endDate || new Date(Date.now() + 86400000).toISOString(),
      timezone: options.timezone || 'UTC',
      showDays: options.showDays !== false,
      showHours: options.showHours !== false,
      showMinutes: options.showMinutes !== false,
      showSeconds: options.showSeconds !== false,
      labels: {
        days: options.labels?.days || 'Days',
        hours: options.labels?.hours || 'Hours',
        minutes: options.labels?.minutes || 'Minutes',
        seconds: options.labels?.seconds || 'Seconds'
      },
      onExpire: options.onExpire || null,
      expiredText: options.expiredText || 'Offer Expired',
      hideOnExpire: options.hideOnExpire || false,
      sectionId: options.sectionId || null
    };

    this.interval = null;
    this.init();
  }

  init() {
    this.render();
    this.start();
  }

  getTimeRemaining() {
    try {
      // Get current time
      const now = new Date();
      
      // Parse the end date and handle timezone
      let targetTime;
      
      if (this.options.timezone !== 'local' && this.options.timezone !== 'UTC') {
        // Handle timezone offset (e.g., "+02:00" or "-05:00")
        const offsetMatch = this.options.timezone.match(/^([+-])(\d{2}):?(\d{2})$/);
        if (offsetMatch) {
          // The end date string should be interpreted as being IN the specified timezone
          // So "2025-12-01T23:59:59" with "-06:00" means 23:59:59 in UTC-6
          const sign = offsetMatch[1] === '+' ? 1 : -1;
          const hours = parseInt(offsetMatch[2]);
          const minutes = parseInt(offsetMatch[3]);
          const timezoneOffsetMs = sign * (hours * 60 + minutes) * 60 * 1000;
          
          // Parse the date string as if it's in the specified timezone
          // Add the timezone to the date string for proper parsing
          const dateWithTimezone = this.options.endDate.includes('Z') || this.options.endDate.match(/[+-]\d{2}:\d{2}$/)
            ? this.options.endDate
            : this.options.endDate + this.options.timezone;
          
          targetTime = new Date(dateWithTimezone).getTime();
        } else {
          // Fallback: treat as local time
          targetTime = new Date(this.options.endDate).getTime();
        }
      } else {
        // For 'local' or 'UTC', parse normally
        targetTime = new Date(this.options.endDate).getTime();
      }
      
      const total = targetTime - now.getTime();
      
      if (total <= 0) {
        return {
          total: 0,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }

      const seconds = Math.floor((total / 1000) % 60);
      const minutes = Math.floor((total / 1000 / 60) % 60);
      const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
      const days = Math.floor(total / (1000 * 60 * 60 * 24));

      return {
        total,
        days,
        hours,
        minutes,
        seconds
      };
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return {
        total: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    }
  }

  render() {
    const time = this.getTimeRemaining();
    
    if (time.total <= 0) {
      // Check if we should hide the entire section
      if (this.options.hideOnExpire && this.options.sectionId) {
        const section = document.getElementById(this.options.sectionId);
        if (section) {
          section.style.display = 'none';
        }
      } else {
        this.element.innerHTML = `<div class="countdown-expired">${this.options.expiredText}</div>`;
      }
      
      this.stop();
      if (this.options.onExpire && typeof this.options.onExpire === 'function') {
        this.options.onExpire();
      }
      return;
    }

    let html = '<div class="countdown-timer-wrapper">';
    
    if (this.options.showDays) {
      html += `
        <div class="countdown-unit countdown-days">
          <div class="countdown-value">${this.pad(time.days)}</div>
          <div class="countdown-label">${this.options.labels.days}</div>
        </div>
      `;
    }
    
    if (this.options.showHours) {
      html += `
        <div class="countdown-unit countdown-hours">
          <div class="countdown-value">${this.pad(time.hours)}</div>
          <div class="countdown-label">${this.options.labels.hours}</div>
        </div>
      `;
    }
    
    if (this.options.showMinutes) {
      html += `
        <div class="countdown-unit countdown-minutes">
          <div class="countdown-value">${this.pad(time.minutes)}</div>
          <div class="countdown-label">${this.options.labels.minutes}</div>
        </div>
      `;
    }
    
    if (this.options.showSeconds) {
      html += `
        <div class="countdown-unit countdown-seconds">
          <div class="countdown-value">${this.pad(time.seconds)}</div>
          <div class="countdown-label">${this.options.labels.seconds}</div>
        </div>
      `;
    }
    
    html += '</div>';
    this.element.innerHTML = html;
  }

  pad(num) {
    return num < 10 ? '0' + num : num;
  }

  start() {
    this.interval = setInterval(() => {
      this.render();
    }, 1000);
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  destroy() {
    this.stop();
    if (this.element) {
      this.element.innerHTML = '';
    }
  }
}

// Auto-initialize timers with data attributes
document.addEventListener('DOMContentLoaded', function() {
  const timerElements = document.querySelectorAll('[data-countdown-timer]');
  
  timerElements.forEach(element => {
    const options = {
      endDate: element.dataset.endDate,
      timezone: element.dataset.timezone || 'UTC',
      showDays: element.dataset.showDays !== 'false',
      showHours: element.dataset.showHours !== 'false',
      showMinutes: element.dataset.showMinutes !== 'false',
      showSeconds: element.dataset.showSeconds !== 'false',
      labels: {
        days: element.dataset.labelDays || 'Days',
        hours: element.dataset.labelHours || 'Hours',
        minutes: element.dataset.labelMinutes || 'Minutes',
        seconds: element.dataset.labelSeconds || 'Seconds'
      },
      expiredText: element.dataset.expiredText || 'Offer Expired',
      hideOnExpire: element.dataset.hideOnExpire === 'true',
      sectionId: element.dataset.sectionId || null
    };
    
    new CountdownTimer(element.id, options);
  });
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CountdownTimer;
}
